import type { SubmissionStatusView } from '@innspel/core';
import { Ikon } from '../Ikon.tsx';
import { Banner, Hint } from './primitiver.tsx';
import { Sporingslinje, sporingsledd } from './Sporingslinje.tsx';
import { formaterFrist, statusVisning } from './status.ts';
import { tekst } from './tekster.ts';

export type KvitteringArt = 'feil' | 'onske' | 'anonym' | 'jaOgsa' | 'kø';

/**
 * 2g — kvitteringen. Den LOVER noe, per spor (DD-07), og saksnummeret er der så
 * innsenderen kan vise til det hos support. Anonym modus lover ikke svar, men
 * saken teller. Ingen «vurder appen», ingen oppfordring til å dele.
 *
 * Presisert 2026-09-14 (C10, D8): ønskesporets tidsløfte er DATOEN fra
 * `deadlineAt` (DD-28), ikke «innen 90 dager» — 90 er leietakerens tak, og det
 * kan kortes. En innsender som leste «90 dager» i appen og «Svar innen 3.
 * desember» i «Mine innspill» hadde fått to løfter.
 *
 * **R7.3 (portal-konsept v0.1, DD-07 presisert 2026-09-20, P1 (a)):** kvitteringen er et
 * KORT — det innsenderen tar vare på. Leietakerens navn og saksnummeret i hjørnet, «Sendt
 * <dato>», og **løftets setning som skjermens helt**. R7.1s brief ba om «Du får svar innen
 * <dato>» som helt, men en ny sak står i Mottatt, og en Mottatt-sak har med vilje ingen
 * frist («Svarfristen starter ikke ved innsending»). Datoen finnes altså ikke ennå; den tar
 * heltens plass i «Mine innspill» den dagen saken er i Vurderes. Sporingslinjen under sier
 * hva som skjer videre.
 *
 * DD-42: feilet vedlegget, er saken likevel sendt. DD-47: live-region; «Lukk»
 * står i dialogens fot (C2) og har fokus, så Enter lukker.
 */
export function Kvittering({
  art,
  saksnummer,
  vedlegg,
  frist,
  sak,
  tenantName,
}: {
  art: KvitteringArt;
  saksnummer?: number;
  /** `feilet`: teksten er lagret, bildet kom ikke med. `forkastet`: køet offline uten bilde. */
  vedlegg?: 'feilet' | 'forkastet';
  /** Svarfristen fra svaret (`deadlineAt`). Tidsløftet er DATOEN, aldri en varighet (DD-28). */
  frist?: string;
  /** Saken slik serveren svarte. Gir sporingslinjen leddene — ingen nye felter. */
  sak?: SubmissionStatusView;
  /** Leietakerens navn på kortet. Utelatt: kortet står med kvitteringens ord, aldri med Innspels navn. */
  tenantName?: string;
  onLukk?: () => void;
}) {
  const innhold = {
    feil: { h: tekst.kvitteringSendt, p: tekst.kvitteringFeil },
    // Løftet er NØYAKTIG det «Mine innspill» og panelet viser for «Vurderes»:
    // det leses fra temaet, så kvitteringen og statusen ikke kan si to ting.
    onske: { h: tekst.kvitteringSendt, p: statusVisning('vurderes', 'onske', frist).lofte },
    anonym: { h: tekst.kvitteringSendtAnonymt, p: tekst.kvitteringAnonym },
    jaOgsa: { h: tekst.kvitteringRegistrert, p: tekst.kvitteringJaOgsa },
    // Køet offline: kvitteringen kan IKKE love svar på noe som ikke er sendt (DD-41).
    // Helten sier hva som KOMMER til å skje; etiketten under sier at det er lagret.
    // «Ingen nett» ville lest som en feil, og det er det ikke.
    kø: { h: tekst.koHelt, p: tekst.koHelt },
  }[art];

  // Køet: ingenting er sendt, så det finnes ingen gang å spore og ingen sak å følge.
  const køet = art === 'kø';
  const ledd = sak && !køet ? sporingsledd(sak, tenantName) : [];
  const sendtDato = sak && !køet ? formaterFrist(sak.createdAt) : undefined;
  const overskrift = vedlegg === 'feilet' ? tekst.kvitteringVedleggFeilet : innhold.h;
  // Linja over helten sier hva som SKJEDDE. For en vanlig innsending er det datoen; for
  // anonym, «ja, jeg også» og køet er ordet selv opplysningen («Sendt anonymt» er ikke det
  // samme som «Sendt»), og da står det der i stedet for — aldri begge, aldri ingen.
  const lagretDato = køet ? formaterFrist(sak?.createdAt ?? new Date().toISOString()) : undefined;
  const lab =
    lagretDato ? tekst.kvitteringLagretDato(lagretDato)
    : sendtDato && art !== 'anonym' && art !== 'jaOgsa' && vedlegg !== 'feilet' ? tekst.kvitteringSendtDato(sendtDato)
    : overskrift;

  return (
    <div className="innspel-stabel">
      <div className="innspel-kvittering" role="status" aria-live="polite" data-testid="kvittering">
        {vedlegg === 'feilet' ? (
          <Banner ikon="warning-outline" tone="warning">
            {tekst.vedleggFeiletBanner}
          </Banner>
        ) : null}
        {vedlegg === 'forkastet' ? (
          <Banner ikon="image-outline" tone="info">
            {tekst.offlineUtenBilde}
          </Banner>
        ) : null}

        <div className="innspel-kvittering-kort" data-tone={køet ? 'neutral' : undefined} data-testid="kvittering-kort">
          <div className="innspel-kvittering-rad">
            <span className="innspel-kvittering-navn">
              <Ikon navn={køet ? 'swap-vertical-outline' : 'checkmark-circle-outline'} />
              {tenantName ?? ''}
            </span>
            {saksnummer ? <span className="innspel-kvittering-nr">{tekst.saksnummer(saksnummer)}</span> : null}
          </div>
          <div>
            <span className="innspel-kvittering-lab">{lab}</span>
            {/* Helten: løftets setning. Datoen tar plassen i «Mine innspill» når den finnes (P1 (a)). */}
            <p className="innspel-kvittering-helt" data-testid="kvittering-tekst">
              {vedlegg === 'feilet' ? `${tekst.tekstenErLagret} ${innhold.p}` : innhold.p}
            </p>
          </div>
          {saksnummer && !køet ? (
            <>
              <span className="innspel-kvittering-strek" aria-hidden="true" />
              <p className="innspel-kvittering-sub">{tekst.kvitteringUnder}</p>
            </>
          ) : køet ? (
            <>
              <span className="innspel-kvittering-strek" aria-hidden="true" />
              {/* DD-41: det køede kortet har ingen sak å spore, men innsenderen skal vite hvor den er. */}
              <p className="innspel-kvittering-sub">{tekst.koUnder}</p>
            </>
          ) : null}
        </div>

        {ledd.length > 0 ? <Sporingslinje ledd={ledd} testId="kvittering-spor" /> : null}
        {saksnummer && vedlegg === 'feilet' ? <Hint>{tekst.saksnummerUtenVedlegg(saksnummer)}</Hint> : null}
      </div>
    </div>
  );
}
